import type { ModuleKey } from "@gaole/shared";
import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  Building2,
  Camera,
  Coins,
  CreditCard,
  FileText,
  Mail,
  Mails,
  Settings,
  Shield,
  Ticket,
} from "lucide-react";

export type SettingsTab =
  | "general"
  | "currencies"
  | "payments"
  | "smtp"
  | "email_templates"
  | "credits"
  | "raffles_auctions"
  | "carduploader"
  | "company"
  | "legal"
  | "security"

export const SETTINGS_TABS: {
  tab: SettingsTab;
  slug: string;
  label: string;
  icon: LucideIcon;
  module?: ModuleKey;
  moduleAny?: ModuleKey[];
}[] = [
  { tab: "general", slug: "general", label: "General", icon: Settings },
  { tab: "currencies", slug: "currencies", label: "Currencies", icon: Banknote },
  { tab: "payments", slug: "payments", label: "Payments", icon: CreditCard },
  { tab: "smtp", slug: "smtp", label: "SMTP", icon: Mail },
  { tab: "email_templates", slug: "email-templates", label: "Email templates", icon: Mails },
  { tab: "credits", slug: "credits", label: "Credits", icon: Coins, module: "credits" },
  {
    tab: "raffles_auctions",
    slug: "raffles-auctions",
    label: "Raffles & Auctions",
    icon: Ticket,
    moduleAny: ["raffles", "auctions", "packs"],
  },
  { tab: "carduploader", slug: "card-uploader", label: "Card Uploader", icon: Camera, module: "card_uploader" },
  { tab: "company", slug: "company", label: "Company", icon: Building2, module: "documents" },
  { tab: "legal", slug: "legal", label: "Legal", icon: FileText },
  { tab: "security", slug: "security", label: "Security", icon: Shield },
];

export const TAB_GROUPS: Record<SettingsTab, string[]> = {
  general: ["general", "storefront", "inventory", "orders"],
  currencies: [],
  payments: ["payments"],
  smtp: ["smtp"],
  email_templates: [],
  credits: ["credits"],
  raffles_auctions: ["raffles", "auctions", "packs"],
  carduploader: ["carduploader"],
  company: ["company"],
  legal: ["legal"],
  security: [],
};

export const GROUP_LABELS: Record<string, string> = {
  general: "General",
  storefront: "Storefront",
  inventory: "Inventory",
  orders: "Orders",
  payments: "Payment gateways",
  shipping: "Shipping",
  smtp: "Email (SMTP)",
  email: "Email templates",
  credits: "Credits",
  raffles: "Raffles",
  auctions: "Auctions",
  packs: "Mystery packs",
  carduploader: "Card uploader",
  company: "Company & tax (documents)",
  legal: "Legal pages",
  security: "Security",
  flexxi: "FlexxiPlan",
  marketplace: "Marketplace",
};

const SLUG_TO_TAB = Object.fromEntries(SETTINGS_TABS.map((entry) => [entry.slug, entry.tab])) as Record<
  string,
  SettingsTab
>;

export function settingsSlugToTab(slug: string | undefined): SettingsTab | null {
  if (!slug) return null;
  return SLUG_TO_TAB[slug] ?? null;
}

export function settingsTabPath(tab: SettingsTab): string {
  const entry = SETTINGS_TABS.find((item) => item.tab === tab);
  return `/admin/settings/${entry?.slug ?? "general"}`;
}

export function settingsTabLabel(tab: SettingsTab): string {
  return SETTINGS_TABS.find((item) => item.tab === tab)?.label ?? "Settings";
}

export function isSettingsTabVisible(
  tab: SettingsTab,
  moduleEnabled: (key: string) => boolean,
): boolean {
  const entry = SETTINGS_TABS.find((item) => item.tab === tab);
  if (!entry) return false;
  if (entry.moduleAny?.length) {
    return entry.moduleAny.some((key) => moduleEnabled(key));
  }
  if (entry.module) return moduleEnabled(entry.module);
  return true;
}
